import React from "react";
import { getDocument, GlobalWorkerOptions } from "pdfjs-dist";
// "pdfjs-dist": "^2.16.105", Need this version only for works the worker

// Set the worker source for PDF.js
GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/2.16.105/pdf.worker.min.js`;

const PasswordModal = ({ onSubmit, onCancel }) => {
    const [error, setError] = React.useState(false);

    const styles = {
        dialog: {
            border: 'none',
            borderRadius: '8px',
            padding: '24px',
            maxWidth: '400px',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
        },
        form: {
            display: 'flex',
            flexDirection: 'column',
            gap: '16px'
        },
        heading: {
            margin: 0,
            color: '#333'
        },
        description: {
            margin: 0,
            color: '#666',
            fontSize: '0.9rem'
        },
        inputGroup: {
            display: 'flex',
            flexDirection: 'column',
            gap: '8px'
        },
        input: {
            padding: '8px 12px',
            border: error ? '1px solid #dc3545' : '1px solid #ddd',
            borderRadius: '4px',
            fontSize: '1rem'
        },
        errorMessage: {
            color: '#dc3545',
            fontSize: '0.8rem'
        },
        buttonGroup: {
            display: 'flex',
            gap: '12px',
            justifyContent: 'flex-end',
            marginTop: '8px'
        },
        cancelButton: {
            padding: '8px 16px',
            borderRadius: '4px',
            border: 'none',
            cursor: 'pointer',
            fontSize: '0.9rem',
            backgroundColor: '#f8f9fa',
            color: '#333'
        },
        submitButton: {
            padding: '8px 16px',
            borderRadius: '4px',
            border: 'none',
            cursor: 'pointer',
            fontSize: '0.9rem',
            backgroundColor: '#007bff',
            color: 'white'
        }
    };

    return (
        <dialog style={styles.dialog} open>
            <form 
                method="dialog" 
                style={styles.form}
                onSubmit={(e) => {
                    e.preventDefault();
                    onSubmit(e.target.pdfPassword.value, setError);
                }}
            >
                <h3 style={styles.heading}>Protected Document</h3>
                <p style={styles.description}>
                    This PDF is password protected. Please enter the password to view it.
                </p>
                
                <div style={styles.inputGroup}>
                    <label htmlFor="pdfPassword">Password:</label>
                    <input 
                        type="password" 
                        id="pdfPassword" 
                        style={styles.input}
                        required 
                        autoFocus
                    />
                    {error && (
                        <span style={styles.errorMessage}>
                            Incorrect password. Please try again.
                        </span>
                    )}
                </div>

                <div style={styles.buttonGroup}>
                    <button 
                        type="button" 
                        onClick={onCancel} 
                        style={styles.cancelButton}
                    >
                        Cancel
                    </button>
                    <button 
                        type="submit" 
                        style={styles.submitButton}
                    >
                        Open Document
                    </button>
                </div>
            </form>
        </dialog>
    );
};

const usePdfLoader = (pdfFile, onClose) => {
    const [pdf, setPdf] = React.useState(null);
    const [pageCount, setPageCount] = React.useState(0);
    const [showPasswordModal, setShowPasswordModal] = React.useState(false);

    const loadPdfWithPassword = async (password = null, setError = null) => {
        try {
            const loadingTask = getDocument(
                password ? { url: pdfFile, password } : pdfFile
            );
            const loadedPdf = await loadingTask.promise;
            setPdf(loadedPdf);
            setPageCount(loadedPdf.numPages);
            setShowPasswordModal(false);
        } catch (error) {
            if (error.name === "PasswordException") {
                if (setError) {
                    setError(true);
                }
                setShowPasswordModal(true);
            } else {
                console.error("Error loading PDF:", error);
            }
        }
    };

    React.useEffect(() => {
        loadPdfWithPassword();
    }, [pdfFile]);

    return {
        pdf,
        pageCount,
        showPasswordModal,
        loadPdfWithPassword,
        setShowPasswordModal,
    };
};

const renderPdfPage = async (pdf, pageNumber, canvas) => {
    if (!pdf || !canvas) return;

    try {
        const page = await pdf.getPage(pageNumber);
        const context = canvas.getContext("2d");
        const cssWidth = canvas.clientWidth;
        const devicePixelRatio = window.devicePixelRatio || 1;
        const scale = cssWidth / page.getViewport({ scale: 1 }).width;
        const viewport = page.getViewport({ scale: scale * devicePixelRatio });

        canvas.width = viewport.width;
        canvas.height = viewport.height;
        context.clearRect(0, 0, canvas.width, canvas.height);

        await page.render({
            canvasContext: context,
            viewport,
        }).promise;
    } catch (error) {
        console.error(`Error rendering page ${pageNumber}:`, error);
    }
};

export const PdfViewer = ({ pdfFile, onClose }) => {
    const containerRef = React.useRef(null);
    const { pdf, pageCount, showPasswordModal, loadPdfWithPassword, setShowPasswordModal } = usePdfLoader(pdfFile, onClose);

    const renderAllPages = React.useCallback(() => {
        if (!containerRef.current || !pdf || pageCount === 0) return;

        // Clear existing canvases
        containerRef.current.innerHTML = '';

        // Create and render new canvases
        Array.from({ length: pageCount }, (_, i) => i + 1).forEach(pageNumber => {
            const canvas = document.createElement("canvas");
            canvas.style.cssText = `
        width: 100%;
        margin-bottom: 20px;
        background: #FFFFFF;
        box-shadow: rgba(99, 99, 99, 0.2) 0px 2px 8px 0px;
      `;
            containerRef.current.appendChild(canvas);
            renderPdfPage(pdf, pageNumber, canvas);
        });
    }, [pdf, pageCount]);

    React.useEffect(() => {
        renderAllPages();
    }, [renderAllPages]);

    return (
        <>
            {showPasswordModal && (
                <PasswordModal
                    onSubmit={(password, setError) => loadPdfWithPassword(password, setError)}
                    onCancel={() => {
                        setShowPasswordModal(false);
                        onClose?.();
                    }}
                />
            )}
            <div
                ref={containerRef}
                style={{
                    width: "100%",
                    height: "100%",
                    overflowY: "auto",
                    overflowX: "hidden",
                }}
            />
        </>
    );
};
